/**
 * English translations — source of truth.
 * All other language files must mirror this structure exactly.
 */
const en = {
    // --- NAVBAR ---
    nav: {
        home: "Home",
        about: "About",
        services: "Services",
        team: "Team",
        contact: "Contact",
        getStarted: "Get Started",
    },

    // --- HERO SECTION ---
    hero: {
        label: "Next-Gen Technology Partner",
        title1: "Delivering Intelligent and",
        title2: "Scalable Digital Solutions",
        description: "Cascade Space specializes in building advanced software, web platforms, and artificial intelligence solutions that empower businesses to innovate, streamline operations, and achieve sustainable growth.",
        cta1: "Explore Our Expertise",
        cta2: "Engage Our Consultants",
        trustPowered: "AI-Powered",
        trustUptime: "99.9% Reliable",
        trustSupport: "Expert Support",
    },

    // --- HOME SECTIONS ---
    home: {
        powLabel: "Years of Innovation",
        powProjects: "Projects Delivered",
        powLive: "Live Products",
        powClient: "Client-Focused",
        statDesc1: "Building since 2023",
        statDesc2: "Across startups & enterprises",
        statDesc3: "Startopia · TextYourBoss.ai · More",
        statDesc4: "Outcome-driven delivery model",

        servicesLabel: "Our Services",
        servicesTitle: "Ready to Transform Your Business?",
        servicesDesc: "Cascade Space delivers cutting-edge technology solutions designed to align with your business objectives. Our expertise spans from digital transformation to artificial intelligence, ensuring measurable outcomes at every stage of engagement.",
        servicesBtn: "Explore Our Services",

        hwwLabel: "OUR APPROACH",
        hwwTitle: "From Complexity to Clarity",
        hwwBody: "We don't deliver black boxes. Every solution we build follows a transparent, structured methodology designed to give you clarity at every stage — from first call to final deployment.",

        techBridgeLabel: "Technical Solutions",
        techBridgeBody: "We guide organizations through comprehensive digital overhauls, migrating legacy systems to modern, scalable architectures.",
        techBridgeCta: "View Our Services",

        featuresLabel: "Feature Cards",
        featuresTitle: "The Cascade Advantage",

        contactLabel: "Get In Touch",
        contactTitle: "Ready to Transform Your Business?",

        // Projects section
        projectsTitle: "Our Innovative Projects",
        projectsDesc: "Discover our cutting-edge platforms and tools designed to solve real-world problems and drive business growth.",
        projectsViewAll: "View All Projects",
        projectsLearnMore: "Learn more",
    },

    // --- ABOUT ---
    about: {
        heroLabel: "About Cascade Space",
        heroTitle: "Empowering Businesses Through Technology & Innovation",
        heroDesc: "We build smart digital solutions that enhance productivity, efficiency, and scalability for individuals, startups, and enterprises.",

        visionLabel: "Future Vision",
        visionTitle: "Becoming a Global Tech Powerhouse",
        missionLabel: "Our Mission",
        missionTitle: "Simplifying Complex Challenges Through Technology",

        visionMissionLabel: "Our Purpose",
        visionBody: "Cascade Space is committed to building a future where innovation transcends boundaries. Our vision is to deliver world-class technology solutions with a transformative impact across industries and communities worldwide.",
        missionBody: "At Cascade Space, our mission is to transform complexity into clarity. We are dedicated to enabling organizations to innovate, expand, and thrive through cost-effective, high-quality digital solutions.",
        visionBullet1: "AI-first solutions for every scale",
        visionBullet2: "Bridging India's digital divide",
        visionBullet3: "Leading next-gen EdTech and FinTech",

        missionLabel2: "Our Mission",
        missionSubtitleH2: "Bridging Ambition with Execution",
        missionBody1: "At Cascade Space, our mission is to decode the complexities of the digital era and deliver streamlined, intelligent systems that amplify human potential and organizational growth.",
        missionBody2: "We believe that true innovation lies not just in adopting the latest technology, but in applying it efficiently to solve real-world problems. Every product we build is designed to scale and endure.",
        missionCardP1: "Executed flawlessly with meticulous attention to scalable architectures.",
        missionCardP2: "Challenging the status quo to craft uniquely brilliant digital solutions.",

        coreValuesLabel: "Core Values",
        valDesc0: "Constantly pushing the boundaries of what's possible with modern tech stacks.",
        valDesc1: "Building architectures designed to grow seamlessly as your business expands.",
        valDesc2: "Fostering an ecosystem where teams and clients have the tools to succeed.",

        teamLabel: "Leadership",
        teamTitle: "The Minds Behind the Tech",
        teamDesc: "A small, relentless team that punches well above its weight class.",

        projectsLabel: "Our Work",
        projectsTitle: "Projects That Define Us",
        projectsBodyDesc: "A showcase of solutions built with precision — every project a testament to our engineering culture.",
        projectsComingSoon: "More innovations coming soon ✦",
        ctaBannerTitle: "Let's Build Something Extraordinary",
        ctaBannerDesc: "Partner with CascadeTech to transform your vision into a world-class digital product.",
    },

    // --- SERVICES ---
    services: {
        heroLabel: "What We Do",
        heroDesc: "From concept to deployment, we craft solutions that are precise, performant, and future-ready.",

        engagementLabel: "Our Engagement Model",
        engagementTitle: "A Proven Path From Idea to Impact",
        engagementBody: "We follow a structured engagement model that removes ambiguity and ensures every project is delivered on time, within scope, and beyond expectations.",

        catDTTitle: "Digital Transformation",
        catDTDesc: "We guide organizations through comprehensive digital overhauls, migrating legacy systems to modern, scalable architectures that improve efficiency and reduce operational overhead.",
        catDTF1: "Enterprise Architecture Migration",
        catDTF2: "Cloud Infrastructure Setup & Optimization",
        catDTF3: "Legacy System Modernization",
        catDTF4: "Workflow Automation",

        catAITitle: "Intelligent AI & Agentic Solutions",
        catAIDesc: "How can AI automate your business? We leverage Large Language Models (LLMs) to automate complex workflows, generate predictive insights, and create hyper-personalized customer experiences at scale.",
        catAIF1: "Custom LLM Fine-Tuning & Integration",
        catAIF2: "Intelligent Conversational Agents",
        catAIF3: "Predictive Analytics Systems",
        catAIF4: "AI-Driven Process Automation",

        catDBTitle: "Design & Branding",
        catDBDesc: "Your digital presence should reflect your ambition. We craft premium, user-centric interfaces and brand identities that resonate with your target audience.",
        catDBF1: "User Interface (UI) Design",
        catDBF2: "User Experience (UX) Research",
        catDBF3: "Brand Identity & Strategy",
        catDBF4: "Interactive Prototypes",

        ctaBannerTitle: "Ready to Build Your Platform?",
        ctaBannerDesc: "Whether you need a complete digital transformation or a specific AI module, our team is ready to execute.",

        cta: "Get a Free Consultation",
        discussService: "Discuss This Service",
    },

    // --- CONTACT ---
    contact: {
        heroLabel: "Get In Touch",
        heroTitle: "Let's Build Something Great Together",
        contactBody: "Have a project in mind or questions about our services? We'd love to hear from you.",

        name: "Your Name",
        email: "Email Address",
        message: "Your Message",
        send: "Send Message",

        formResponseTime: "We'll get back to you within 24–48 hours.",
        businessHours: "Monday–Friday, 9am–5pm IST",
        hqTitle: "CascadeTech Solutions HQ",
        hqLocation: "Vidya-Nagar, Hubballi",
    },

    // --- PROJECTS PAGE ---
    projectsPage: {
        heroLabel: "Portfolio & Case Studies",
        heroDesc: "Explore our portfolio of digital platforms and tools designed to solve real-world problems and accelerate business growth.",

        startopiaTitle: "Startopia",
        startopiaDesc: "A comprehensive ecosystem designed to empower founders with resources, investor connections, and highly scalable technological platforms.",
        startopiaFeat1: "End-to-End Encryption",
        startopiaFeat2: "Automated Matchmaking",
        startopiaFeat3: "Real-time Analytics",
        startopiaFeat4: "Scalable Microservices",
        startopiaPreview: "Startopia Preview",

        textYourBossTitle: "TextYourBoss.ai",
        textYourBossDesc: "An AI-powered communication tool simplifying professional messaging, workflow automation, and context-aware responses for enterprise teams.",
        textYourBossFeat1: "Context-Aware AI Generation",
        textYourBossFeat2: "Tone Adjustment Engine",
        textYourBossFeat3: "Multi-Platform Integration",
        textYourBossFeat4: "Enterprise Role-Based Access",
        textYourBossPreview: "TextYourBoss.ai Interface",

        comingSoonLabel: "Innovation Pipeline",
        futureProj1Title: "Renewable Energy Platform",
        futureProj1Desc: "An IoT-enabled dashboard for monitoring and optimizing solar and wind farm performance in real-time.",
        futureProj1Status: "In Development",

        futureProj2Title: "Algorithmic Trading Bot",
        futureProj2Desc: "A high-frequency trading algorithm leveraging machine learning to execute complex financial strategies.",
        futureProj2Status: "Beta Testing",

        futureProj3Title: "Autonomous Robotics API",
        futureProj3Desc: "A standardized communication API enabling seamless integration between varied industrial robotics systems.",
        futureProj3Status: "Research Phase",

        ctaBannerTitle: "Have a Project in Mind?",
        ctaBannerDesc: "Let's collaborate to build something extraordinary that pushes the boundaries of your industry.",
    },

    // --- TEAM PAGE ---
    teamPage: {
        heroLabel: "Our Leadership",
        heroDesc: "Meet our visionary leaders who are transforming how businesses leverage technology for growth and innovation.",

        akshayBio: "Akshay Kumar Ankalagi is the visionary CEO of CascadeTech Solutions, bringing extensive experience in technology leadership and business transformation. His strategic insights and forward-thinking approach have positioned CascadeTech as a leader in digital innovation. Akshay specializes in identifying market opportunities and developing solutions that create lasting value for clients across industries.",
        manojBio: "Manoj Relekar serves as the operational backbone of CascadeTech Solutions, bringing exceptional organizational leadership and process optimization expertise. With a background spanning technology and business operations, Manoj ensures that our service delivery consistently exceeds client expectations. His methodical approach to scaling operations has been instrumental in CascadeTech's sustainable growth trajectory.",
        vaibhavaBio: "Vaibhava BG leads CascadeTech's technical innovation as CTO, combining deep technical expertise with creative problem-solving abilities. His passion for emerging technologies like AI, blockchain, and cloud architecture drives our product development roadmap. Vaibhava's leadership in building scalable, secure digital platforms has been crucial in delivering transformative solutions for our clients' most complex challenges.",

        coreValuesLabel: "Our Principles",
        coreValuesTitle: "The Ideals That Anchor Us",
        valInnovationDesc: "Constantly exploring new technologies and approaches.",
        valExcellenceDesc: "Exceeding expectations with precision and attention to detail.",
        valIntegrityDesc: "Building trust through honest, transparent practices.",
        valCollaborationDesc: "Working together across disciplines for comprehensive solutions.",

        ctaBannerTitle: "Join Our Growing Ecosystem",
        ctaBannerDesc: "We are always looking for visionary clients and brilliant minds to collaborate with.",
    },

    // --- NOT FOUND ---
    notFound: {
        title: "System Node Not Found",
        desc: "The trajectory you've requested doesn't exist within our digital infrastructure. Please return to the core module.",
        backToHome: "Initialize Core Sequence",
    },

    // --- FOOTER ---
    footer: {
        desc: "Empowering businesses through cutting-edge technology, specializing in web design, software development, and intelligent digital solutions.",
        company: "Company",
        services: "Services",
        contact: "Contact",
        rights: "CascadeTech Solutions. All rights reserved.",
        tagline: "Built with precision · Engineered to scale",
    },

    // --- GENERIC CTA ---
    cta: {
        startProject: "Start Your Project",
        learnMore: "Learn More",
        getInTouch: "Get In Touch",
    },

    // --- HEADINGS SUPPLEMENT ---
    headings: {
        techTitle: "See Our Services in Action",
        emailUs: "Email Us",
        visitUs: "Visit Us",
        infoEmail: "Email",
        infoPhone: "Phone",
        infoAddress: "Address",
        infoHours: "Business Hours",
        formTitle: "Send Us a Message",
        missionSubtitle: "Bridging Ambition with Execution",
        valPrecision: "Precision",
        valInnovation: "Innovation",
        valExcellence: "Excellence",
        valIntegrity: "Integrity",
        valCollaboration: "Collaboration",
        visionSubtitle: "Architecting Tomorrow",
        projectsHeroTitle: "Innovative Solutions That Drive Results",
        teamHeroTitle: "The Team Behind CascadeTech Solutions",
        ctaBannerTitle: "Ready to Transform Your Digital Presence?",
        comingSoon: "Preparing for Launch",
        servicesHeroH1: "Intelligent Solutions for Modern Enterprise",
        discussService: "Discuss This Service",
        viewCaseStudy: "View Case Study",
        valuesSectionTitle: "What Drives Us Forward",
        builtOnPrinciple: "Built on Principle",
        mindsBehinTech: "The Minds Behind the Tech",
        contactHeroTitle: "Let's Build Something Great Together",
        intelligence: "Intelligence",
        missionPillarSpeed: "Speed",
        missionPillarReliability: "Reliability",
        missionPillarReach: "Reach",
        missionPillarIntelligence: "Intelligence",
        futureVisionBody: "We look beyond the immediate constraints of technology. By investing in AI ecosystems, smart automation, and renewable tech solutions, Cascade Space is committed to building sustainable infrastructure that shapes the next decade of digital growth.",
        futureVisionCta: "View Our Upcoming Innovations",
    },

    // --- BODY TEXT ---
    body: {
        labelName: "Name",
        labelEmail: "Email",
        labelCompany: "Company",
        labelMessage: "Message",
        optional: "(optional)",
        placeholderName: "John Doe",
        placeholderEmail: "john@company.com",
        placeholderCompany: "Your Organisation",
        placeholderMessage: "Tell us about your project or ask us anything...",
        formSubDesc: "Fill out the form below and we'll get back to you as soon as possible.",
        contactDesc: "Have a project in mind or questions about our services? We'd love to hear from you.",
        contactTeamReady: "Whether you have a specific project or need strategic guidance, our team is ready.",
        callUs: "Call Us",
        visionBody: "To be the most trusted technology partner for businesses across India and beyond — building intelligent, accessible digital ecosystems.",
        missionBody: "To decode the complexities of the digital era and deliver streamlined, intelligent systems that amplify human potential.",
        visionBullet1: "AI-first solutions for every scale",
        visionBullet2: "Bridging India's digital divide",
        visionBullet3: "Leading next-gen EdTech and FinTech",
        teamSmallDesc: "A small, relentless team that punches well above its weight class.",
        meetFullTeam: "Meet the Full Team",
        projectsComingSoon: "More innovations coming soon ✦",
        keyFeatures: "Key Features",
        heroTeamDesc: "Passionate leaders committed to transforming businesses through technology.",
        exploreExpertise: "Explore Our Expertise",
        engageConsultants: "Engage Our Consultants",
        sendMessage: "Send Message",
        ourPurpose: "Our Purpose",
        coreValues: "Core Values",
        viewPortfolio: "View Full Portfolio",
        showcaseDesc: "A showcase of solutions built with precision — every project a testament to our engineering culture.",
        footerSvcWebDev: "Website Development",
        footerSvcWebDesign: "Web Design & UI/UX",
        footerSvcSoftDev: "Software Development",
        footerSvcAI: "AI & Chatbot Development",
        footerSvcEdTech: "Educational Technology",
        footerSvcLoyalty: "Loyalty Program Solutions",

        // TechBridge benefits
        techBridgeBen1: "End-to-end digital transformation",
        techBridgeBen2: "AI-powered automation & insights",
        techBridgeBen3: "Scalable cloud architecture",
        techBridgeBen4: "Premium UI/UX design systems",

        // HowWeWork step bodies (steps 1–4)
        hwwStepBody1: "We begin by mapping your business landscape — your goals, constraints, and the real problems beneath the surface. No assumptions, no shortcuts.",
        hwwStepBody2: "Our technical team designs a scalable, future-proof solution blueprint. Every technology decision is deliberate, documented, and aligned to your business outcomes.",
        hwwStepBody3: "We build in sprints with continuous feedback loops. You see progress, not just promises — with working deliverables at every milestone.",
        hwwStepBody4: "Deployment is the beginning, not the end. We provide post-launch support, performance monitoring, and iterative improvements as your business scales.",

        // HowWeWork main body
        howWeWorkBody: "We follow a structured, iterative approach — from discovery to deployment — ensuring every solution is precise, scalable, and future-ready.",

        // Project descriptions for TechBridge section
        startopiaShortDesc: "A comprehensive ecosystem designed to empower founders with resources and scalable platforms.",
        textYourBossShortDesc: "An AI-powered communication tool simplifying professional messaging and workflow automation.",

        // Full project descriptions
        startopiaFullDesc: "A comprehensive platform connecting startups with investors, mentors, and essential services to accelerate growth and secure funding.",
        textYourBossFullDesc: "An AI-powered platform providing instant business guidance, decision support, and strategic insights to entrepreneurs and executives.",

        // Project tags
        tagStartupGrowth: "Startup Growth",
        tagInvestorMatching: "Investor Matching",
        tagMentorship: "Mentorship",
        tagFunding: "Funding",
        tagAI: "Artificial Intelligence",
        tagBusinessGuidance: "Business Guidance",
        tagDecisionSupport: "Decision Support",
        tagStrategy: "Strategy",
    },

    // --- ARRAYS ---
    hwwSteps: [
        "Precision Engineering",
        "Launch & Scale",
    ],

    homeFeatures: [
        "Digital Transformation",
        "Startup Ecosystem",
        "AI & Smart Solutions",
        "Design & Branding",
    ],

    homeFeatureDescs: [
        "Strengthen your organization's digital foundation through enterprise-grade websites, mobile applications, and intelligent automation frameworks.",
        "Enable founders to access investors, resources, and scalable platforms that foster accelerated growth and sustainable business outcomes.",
        "Deploy intelligent systems that automate critical workflows, generate actionable insights, and deliver measurable returns on investment.",
        "Craft sophisticated user experiences, distinctive brand identities, and strategic communication assets aligned with organizational objectives.",
    ],

    servicesList: [
        "Website Development",
        "Web Design & UI/UX",
        "Software Development",
        "AI & Chatbot",
        "Loyalty Programs",
        "Educational Tech",
    ],

    servicesListDescs: [
        "Custom website development tailored to your unique business needs and goals.",
        "Visually stunning designs with intuitive user experiences that engage your audience.",
        "Custom software solutions that streamline operations and drive business growth.",
        "Intelligent conversational interfaces to enhance customer experience and engagement.",
        "Comprehensive solutions to build and maintain customer loyalty for your business.",
        "Innovative digital solutions to enhance learning experiences and educational outcomes.",
    ],

    timelineSteps: [
        "Discovery Call",
        "Strategy & Scoping",
        "Design Sprint",
        "Build & Iterate",
        "Launch & Support",
    ],

    timelineStepBodies: [
        "A focused conversation to understand your goals, constraints, and success metrics.",
        "We produce a detailed technical scope, timeline, and investment breakdown. No surprises.",
        "High-fidelity wireframes and design prototypes reviewed and approved before a single line of code is written.",
        "Agile sprints with weekly demos. You have full visibility into progress at all times.",
        "Deployment, monitoring, performance optimization, and continued partnership for growth.",
    ],

    valuesSectionValues: [
        "Innovation",
        "Scalability",
        "Empowerment",
    ],
};

export default en;
export type TranslationSchema = typeof en;
